import { TokenPairSchema } from '@aysar/contracts';
import { fetchBaseQuery, type BaseQueryFn, type FetchArgs } from '@reduxjs/toolkit/query';

import { loggedOut, tokensRefreshed } from '@/shared/store/reducers/auth/auth.slice';
import { type AuthRoot } from '@/shared/store/reducers/auth/auth.types';
import { env } from '@/lib/env.config';
import { type PreferencesRoot } from '@/shared/store/reducers/preferences/preferences.types';
import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

/** The slices this base query reads. Structural, so it never imports the store (W4). */
type BaseQueryRoot = AuthRoot & PreferencesRoot;

/**
 * The three endpoints whose 401 is an ANSWER rather than an expiry.
 *
 * A 401 from `/auth/login` means the credentials were wrong; from `/auth/refresh`
 * that the refresh token is spent or revoked; from `/auth/logout` that the token
 * being surrendered is already gone. Refreshing on any of them turns one refused
 * sign-in into a second pointless round trip — and on `/auth/refresh` it would
 * be an unbounded recursion.
 */
const NO_REFRESH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout'] as const;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.apiBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as BaseQueryRoot;

    if (state.auth.accessToken !== null) {
      headers.set('authorization', `Bearer ${state.auth.accessToken}`);
    }

    // Sent on EVERY request, the default locale included. `localeOf()` on the
    // api reads the first two characters and falls back to `ar`, so omitting it
    // would work — right up until the fallback changes and every admin response
    // silently switches language. Read from the preferences slice rather than
    // from i18next, which keeps the data layer free of an i18n import.
    headers.set('accept-language', state.preferences.locale);

    return headers;
  },
});

/**
 * The in-flight refresh, shared by every caller.
 *
 * Module scope on purpose: a mutex library would buy nothing over one promise,
 * and the invariant is small enough to state in a sentence — while this is
 * non-null, a refresh is running, and everyone waits for it rather than spending
 * a refresh token that is already being rotated. Spending it twice is what the
 * api's reuse detection reads as theft, and its answer is to revoke every
 * session the actor has (P2).
 */
let refreshInFlight: Promise<boolean> | null = null;

/** Test-only: a spec must not inherit the previous spec's in-flight refresh. */
export function resetRefreshState(): void {
  refreshInFlight = null;
}

function pathOf(args: string | FetchArgs): string {
  return typeof args === 'string' ? args : args.url;
}

function isRefreshExempt(args: string | FetchArgs): boolean {
  const path = pathOf(args);
  return NO_REFRESH_PATHS.some((exempt) => path.startsWith(exempt));
}

type Api = Parameters<BaseQueryFn>[1];

async function performRefresh(api: Api, extraOptions: object): Promise<boolean> {
  const refreshToken = (api.getState() as BaseQueryRoot).auth.refreshToken;
  if (refreshToken === null) return false;

  const result = await rawBaseQuery(
    { url: '/auth/refresh', method: 'POST', body: { refreshToken } },
    api,
    extraOptions,
  );

  // The envelope has not been unwrapped yet at this level, so reach through it.
  const payload = unwrapEnvelope(result.data);
  const pair = TokenPairSchema.safeParse(payload);

  if (!pair.success) return false;

  api.dispatch(
    tokensRefreshed({
      accessToken: pair.data.accessToken,
      refreshToken: pair.data.refreshToken,
    }),
  );
  return true;
}

/**
 * Runs at most one refresh at a time and resolves every caller with its outcome.
 *
 * `finally` clears the slot rather than a `then`, so a refresh that throws does
 * not wedge every later request behind a promise that will never settle again.
 */
function ensureRefreshed(api: Api, extraOptions: object): Promise<boolean> {
  refreshInFlight ??= performRefresh(api, extraOptions).finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

function unwrapEnvelope(payload: unknown): unknown {
  if (
    typeof payload === 'object' &&
    payload !== null &&
    'success' in payload &&
    'data' in payload
  ) {
    return (payload as { data: unknown }).data;
  }
  return payload;
}

/**
 * The one base query every RTK Query api uses (W4).
 *
 * Three things happen here and nowhere else: the bearer and `Accept-Language`
 * headers go on, the success envelope comes off, and an expired session is
 * refreshed exactly once before the request is retried exactly once.
 */
export const baseQueryWithAuth: BaseQueryFn<string | FetchArgs, unknown, ApiError> = async (
  args,
  api,
  extraOptions,
) => {
  // A request issued while a refresh is running waits for it, so it is built
  // with the rotated token instead of racing the one being replaced. Without
  // this, every request in flight when a token expires spends its own refresh.
  if (refreshInFlight !== null) {
    await refreshInFlight;
  }

  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401 && !isRefreshExempt(args)) {
    const refreshed = await ensureRefreshed(api, extraOptions);

    if (refreshed) {
      result = await rawBaseQuery(args, api, extraOptions);
    } else {
      // Whatever the reason — expired, revoked, reused, or a body that is not a
      // token pair — there is no session left to salvage. The listener on
      // `loggedOut` clears storage and resets every api cache; the reason
      // is what the login page says instead of a silent bounce.
      api.dispatch(loggedOut('expired'));
    }
  }

  return result.error === undefined
    ? { data: unwrapEnvelope(result.data) }
    : { error: toApiError(result.error) };
};
